import { useState } from 'react';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Portal } from '@headlessui/react';
import {
  CashIcon,
  TrashIcon,
  CheckIcon,
  EyeOffIcon,
  PencilIcon,
  CalendarIcon,
  DuplicateIcon,
  DocumentTextIcon,
  DotsHorizontalIcon,
} from '@heroicons/react/outline';
import { usePopper } from 'react-popper';

import {
  ProviderService,
  PatientAccessLevel,
  PublicProviderServiceDataFragment,
} from '../../../generated/graphql';

import { shouldUseProviderPrimaryColor } from '../../lib/colors';
import { parseMarkdown } from '../../lib/markdown';
import { handleCopyMyPage, urlFromSlug } from '../../../lib/storefront';

import Button from '../Button';
import IconButton from '../IconButton';
import TooltipIcon from './TooltipIcon';
import { CALENDLY_COLOR_MAP, getIconComponent } from './helpers';

interface ServiceCardProps {
  service?: ProviderService;
  publicService?: PublicProviderServiceDataFragment;
  handleDeleteService?: (service: ProviderService) => void;
  providerPrimaryColor?: string | null;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  publicService,
  handleDeleteService,
  providerPrimaryColor,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (publicService) {
    providerPrimaryColor = publicService?.providerPrimaryColor;
  }

  const useProviderPrimaryColor =
    shouldUseProviderPrimaryColor(providerPrimaryColor);

  // Use either service or publicService data
  const serviceDisplay = service || publicService;

  const [menuButtonElement, setMenuButtonElement] =
    useState<HTMLElement | null>(null);
  const [menuPopperElement, setMenuPopperElement] =
    useState<HTMLElement | null>(null);

  const { styles: menuStyles, attributes: menuAttributes } = usePopper(
    menuButtonElement,
    menuPopperElement,
    {
      placement: 'bottom-start',
      strategy: 'absolute',
      modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
    },
  );

  const isNotAllAccess =
    serviceDisplay?.patientAccessLevel !== PatientAccessLevel.All;
  const isNewPatientsAccess =
    serviceDisplay?.patientAccessLevel === PatientAccessLevel.New;
  const isCurrentPatientsAccess =
    serviceDisplay?.patientAccessLevel === PatientAccessLevel.Current;

  const serviceUrl = urlFromSlug(serviceDisplay?.slug);

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a')) {
      return;
    }
    if (service) {
      navigate(`/services/${service.id}`, {
        state: { from: location.pathname },
      });
    } else if (publicService) {
      navigate(`/p/${publicService.slug}`);
    }
  };

  const IconComponent = getIconComponent(serviceDisplay?.serviceIcon);

  const [isCopyingLink, setIsCopyingLink] = useState(false);

  return (
    <div
      className={classNames(
        'w-full cursor-pointer rounded-lg bg-white shadow-lg shadow-neutral-75 transition-shadow hover:shadow-100',
        service && 'h-auto md:h-[382px] md:w-[382px]',
        publicService && 'h-auto min-h-[320px] md:w-[382px]',
        service && !service?.isEnabled && 'opacity-50',
      )}
      onClick={handleCardClick}
    >
      <div className="flex h-full flex-col px-8 py-6">
        {/* Icon & Price */}
        <div className="mb-4 flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-end justify-start">
            <IconComponent
              className={classNames(
                'h-16 w-16',
                serviceDisplay.serviceIconColorUseCalendlyDefault &&
                  serviceDisplay.calendlyEventColor
                  ? CALENDLY_COLOR_MAP[serviceDisplay.calendlyEventColor]
                  : useProviderPrimaryColor
                  ? 'text-primary-125'
                  : 'text-blue-100',
              )}
            />
            {isNotAllAccess && (
              <div
                className={classNames(
                  'ml-3 rounded-full px-3 py-1 text-small-caption',
                  isNewPatientsAccess && 'bg-green-25 text-green-150',
                  isCurrentPatientsAccess && 'bg-blue-25 text-blue-150',
                )}
              >
                {isNewPatientsAccess && 'New clients only'}
                {isCurrentPatientsAccess && 'Current clients only'}
              </div>
            )}
          </div>
          {serviceDisplay?.isPriceLabelShown !== false && (
            <div className="flex flex-col items-end gap-1">
              <span className="text-category font-medium text-green-150">
                {serviceDisplay?.isPaid && serviceDisplay?.price
                  ? `$${serviceDisplay?.price.toLocaleString()}`
                  : 'Free'}
              </span>
            </div>
          )}
        </div>

        {/* Title and Price */}
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="font-serif text-subtitle-small font-light text-neutral-200">
            {serviceDisplay?.name}
          </h3>
        </div>

        {/* Duration */}
        {Boolean(serviceDisplay?.durationMinutes) && (
          <p className="mb-2 text-small-caption text-neutral-110">
            {serviceDisplay?.durationMinutes} minutes
          </p>
        )}

        {/* Description — markdown via parseMarkdown (same pattern as NoteCard, ProviderNote) */}
        <div
          className={classNames(
            'prose prose-sm mt-1 max-w-none break-words text-caption text-neutral-125 line-clamp-5',
            '[&_a]:text-secondary-50 [&_p]:!my-0 [&_ul]:!my-0 [&_ol]:!my-0 [&_li]:!my-0',
          )}
          dangerouslySetInnerHTML={{
            __html: parseMarkdown(serviceDisplay?.description ?? ''),
          }}
        />

        {/* Bottom Actions */}
        {service && (
          <div className="mt-auto pt-2">
            <div className="flex items-center">
              <div className="flex gap-1.5">
                {service.calendlyEventTypeUri && (
                  <TooltipIcon
                    IconComponent={CalendarIcon}
                    tooltipText="Calendly event attached"
                    iconClassName="h-4 w-4 text-blue-125"
                    backgroundColor="bg-blue-25"
                  />
                )}
                {service.isPaid && (
                  <TooltipIcon
                    IconComponent={CashIcon}
                    tooltipText="Paid service"
                    iconClassName="h-4 w-4 text-neutral-125"
                    backgroundColor="bg-neutral-50"
                  />
                )}
                {service.intakeFormAssessment && (
                  <TooltipIcon
                    IconComponent={DocumentTextIcon}
                    tooltipText="Intake form attached"
                    iconClassName="h-4 w-4 text-green-125"
                    backgroundColor="bg-purple-25"
                  />
                )}
                {!service.isShownOnStorefront && (
                  <TooltipIcon
                    IconComponent={EyeOffIcon}
                    tooltipText="Hidden from your page"
                    iconClassName="h-4 w-4 text-orange-125"
                    backgroundColor="bg-orange-50"
                  />
                )}
              </div>

              <div className="flex-1" />

              {serviceUrl && (
                <Button
                  title="Copy link"
                  IconComponent={isCopyingLink ? CheckIcon : DuplicateIcon}
                  iconClassName={isCopyingLink && 'text-green-150'}
                  iconPosition="left"
                  size="small"
                  noFocus
                  theme="secondary-white"
                  disabled={!service?.isEnabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCopyingLink(true);
                    handleCopyMyPage(serviceUrl);
                    setTimeout(() => {
                      setIsCopyingLink(false);
                    }, 3000);
                  }}
                  circular
                />
              )}

              <Menu as="div" className="relative">
                <Menu.Button
                  ref={setMenuButtonElement}
                  className="flex h-8 w-8 items-center justify-center text-neutral-125 hover:text-neutral-150"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    IconComponent={DotsHorizontalIcon}
                    className="h-6 w-6 rotate-90 text-neutral-125"
                    aria-label={'Service actions'}
                  />
                </Menu.Button>

                <Portal>
                  <Menu.Items
                    ref={setMenuPopperElement}
                    style={menuStyles.popper}
                    {...menuAttributes.popper}
                    className="z-50 w-48 divide-y divide-neutral-75 rounded-md border border-neutral-75 bg-white shadow-lg focus:outline-none"
                  >
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              'flex w-full items-center px-4 py-2 text-caption',
                              active
                                ? 'bg-neutral-25 text-neutral-150'
                                : 'text-neutral-125',
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/services/${service.id}`, {
                                state: { from: location.pathname },
                              });
                            }}
                          >
                            <PencilIcon className="mr-3 h-4 w-4" />
                            Edit service
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={classNames(
                              'flex w-full items-center px-4 py-2 text-caption text-red-100',
                              active && 'bg-neutral-25',
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteService?.(service);
                            }}
                          >
                            <TrashIcon className="mr-3 h-4 w-4" />
                            Delete service
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Portal>
              </Menu>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceCard;
